// Shared helpers for ICD-10 compliance-grading display. The source of truth for
// per-code evidence lives on `chat.contextVariables["local:billableCodesICD10"]`,
// populated by the backend "Note - Hub" / "AI Update Note" processes. Both the
// Optimize modal and the voice picker render the same SPEC% / HPI / PLAN / V28
// signals — so the tier thresholds live here, not duplicated per component.

// 0–100 specificity → one of "low" | "medium" | "high".
// Matches the legacy ModalIC10Optimizer thresholds exactly so visual treatment
// stays consistent across both modals.
export function getSpecLevel(val) {
  let level = "low";
  if (val >= 60) level = "medium";
  if (val >= 80) level = "high";
  return level;
}

// 0–2 evidence integer (HPI or Plan of Care coverage) → one of "red" | "orange" | "green".
// Matches the legacy getHpiLevel thresholds.
export function getEvidenceLevel(val) {
  let level = "red";
  if (val >= 1) level = "orange";
  if (val >= 2) level = "green";
  return level;
}

// Flatten a pre-scored ICD-10 list (top-level codes plus their nested
// `alternatives`) into a Map keyed by uppercased leading code. Used by the
// voice picker to decorate each candidate row with its precomputed grading
// without a new backend round-trip. First occurrence wins.
export function buildEvidenceMap(scoredList) {
  const map = new Map();
  if (!Array.isArray(scoredList)) return map;
  const add = (item) => {
    if (!item) return;
    const key = String(item.code || "")
      .trim()
      .toUpperCase();
    if (!key || map.has(key)) return;
    map.set(key, {
      specificity: item.specificity,
      v28: !!item.v28,
      hpi: item.evidence?.hpi,
      planOfCare: item.evidence?.planOfCare,
    });
  };
  for (const item of scoredList) {
    add(item);
    for (const alt of item.alternatives || []) add(alt);
  }
  return map;
}

// Lookup an evidence record for a full picker label like "M17.11 - Unilateral ...".
// Returns null when no grading exists for the code (e.g. a brand-new voice
// suggestion the backend has not yet scored).
export function evidenceForLabel(label, evidenceMap) {
  if (!label || !evidenceMap) return null;
  const code = String(label.split(" - ")[0] || "")
    .trim()
    .toUpperCase();
  if (!code) return null;
  return evidenceMap.get(code) || null;
}

// Map uppercased code -> non-empty alternatives[] from a normalized ICD-10 list.
// Only alternatives with a REAL description are restorable: the snapshot keeps
// the LLM's raw keys, and when those are bare codes ("S82.62XA") normalizList
// derives description === code — restoring such an entry renders (and, once a
// provider selects it, saves) "S82.62XA - S82.62XA" into the note/EHR.
function buildAlternativesByCode(normalizedList) {
  const map = new Map();
  if (!Array.isArray(normalizedList)) return map;
  for (const item of normalizedList) {
    const key = String(item?.code || "")
      .trim()
      .toUpperCase();
    if (!key || map.has(key)) continue;
    const usable = Array.isArray(item?.alternatives)
      ? item.alternatives.filter((alt) => {
          const code = String(alt?.code || "").trim();
          const description = String(alt?.description || "").trim();
          return code && description && description.toUpperCase() !== code.toUpperCase();
        })
      : [];
    if (usable.length > 0) map.set(key, usable);
  }
  return map;
}

// Restore alternatives stripped from the working `billableCodesICD10` list using
// the backend's pre-validation snapshot (`billableCodesICD10Initial`). The
// "Validate ICD10 w_alternatives" backend step deletes alternatives whose DB
// lookup fails (historically ALL of them, via a malformed "CODE - " lookup key —
// fixed in lucasai-backend#268; hallucinated codes are still deleted), leaving a
// code's `alternatives` empty — so the optimizer renders "No alternative codes
// found" even though the snapshot still holds the set. For each working code
// whose alternatives are empty, pull the USABLE ones from the snapshot by code.
// Codes that still have alternatives (or aren't in the snapshot) are untouched.
export function restoreAlternatives(workingList, initialList) {
  if (!Array.isArray(workingList)) return [];
  const byCode = buildAlternativesByCode(initialList);
  if (byCode.size === 0) return workingList;
  return workingList.map((item) => {
    const hasAlternatives = Array.isArray(item?.alternatives) && item.alternatives.length > 0;
    if (hasAlternatives) return item;
    const fallback = byCode.get(
      String(item?.code || "")
        .trim()
        .toUpperCase()
    );
    return fallback && fallback.length > 0 ? { ...item, alternatives: fallback } : item;
  });
}
